import { Component, ChangeDetectionStrategy, input, output, computed, DestroyRef, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { TreeSelect, TreeSelectModule } from 'primeng/treeselect';
import { TreeNode } from 'primeng/api';

import { AuditFilterState, IFilterChoice, IOrganization } from 'src/app/core/models/audit-event';

@Component({
  selector: 'app-audit-log-filters',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonsModule, DropDownsModule, DateInputsModule, TreeSelectModule],
  templateUrl: './audit-log-filters.component.html',
  styleUrl: './audit-log-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogFiltersComponent {
  private destroyRef = inject(DestroyRef);
  private searchSubject = new Subject<string>();
  private treeFilterSub?: Subscription;
  private hasActiveOrgSearch = false;

  @ViewChild('orgTreeSelect') private orgTreeSelect?: TreeSelect;

  // Filter choice data from parent
  organizations = input<IOrganization[]>([]);
  isLoadingOrganizations = input<boolean>(false);
  actions = input<IFilterChoice[]>([]);
  outcomes = input<IFilterChoice[]>([]);
  eventTypes = input<IFilterChoice[]>([]);
  userTypes = input<IFilterChoice[]>([]);

  // Events to parent
  onApplyFilters = output<AuditFilterState>();
  onResetFilters = output<void>();
  onOrganizationSearch = output<string>();

  constructor() {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((query) => this.onOrganizationSearch.emit(query));

    this.destroyRef.onDestroy(() => this.treeFilterSub?.unsubscribe());
  }

  // PrimeNG TreeSelect data — computed from organizations input
  protected readonly organizationTreeData = computed<TreeNode[]>(() => {
    return this.transformToTreeData(this.organizations());
  });

  // PrimeNG TreeSelect model (array of selected TreeNode objects)
  protected selectedOrganizationsModel: TreeNode[] | null = null;

  // Internal filter state (ngModel-bound)
  selectedOutcome = '';
  selectedAction = '';
  selectedEventType = '';
  selectedUserType = '';
  startDate: Date | null = null;
  endDate: Date | null = null;

  /**
   * Check if any filter is active — used to disable Clear button.
   * Aligned with analytics-filter's hasActiveFilters() pattern.
   */
  hasActiveFilters(): boolean {
    return !!(
      this.getSelectedOrganizationIds().length > 0 ||
      this.selectedOutcome ||
      this.selectedAction ||
      this.selectedEventType ||
      this.selectedUserType ||
      this.startDate ||
      this.endDate
    );
  }

  /**
   * Build typed filter state and emit to parent.
   */
  applyFilters(): void {
    const state: AuditFilterState = {
      organizationIds: this.getSelectedOrganizationIds(),
      outcome: this.selectedOutcome,
      action: this.selectedAction,
      eventType: this.selectedEventType,
      userType: this.selectedUserType,
      startDate: this.startDate,
      endDate: this.endDate,
    };
    this.onApplyFilters.emit(state);
  }

  /**
   * Reset all internal filter state and notify parent.
   */
  resetFilters(): void {
    this.selectedOrganizationsModel = null;
    this.selectedOutcome = '';
    this.selectedAction = '';
    this.selectedEventType = '';
    this.selectedUserType = '';
    this.startDate = null;
    this.endDate = null;
    this.onResetFilters.emit();
  }

  /**
   * Gets the count of selected organizations for chip display.
   * Aligned with analytics-filter's getSelectedOrganizationCount().
   */
  protected getSelectedOrganizationCount(): number {
    if (!this.selectedOrganizationsModel || !Array.isArray(this.selectedOrganizationsModel)) {
      return 0;
    }
    return this.selectedOrganizationsModel.length;
  }

  /**
   * Gets the first selected node for chip display.
   * Aligned with analytics-filter's getVisibleSelectedNodes().
   */
  protected getVisibleSelectedNodes(): TreeNode[] {
    if (!this.selectedOrganizationsModel || !Array.isArray(this.selectedOrganizationsModel)) {
      return [];
    }
    return this.selectedOrganizationsModel.slice(0, 1);
  }

  /**
   * Removes a node from the selection.
   * Aligned with analytics-filter's removeNode().
   */
  protected removeNode(event: Event, node: TreeNode): void {
    event.stopPropagation();
    if (this.selectedOrganizationsModel && Array.isArray(this.selectedOrganizationsModel) && node.key) {
      this.selectedOrganizationsModel = this.selectedOrganizationsModel.filter((n) => n.key !== node.key);
    }
  }

  /**
   * Wire up server-side search when the TreeSelect overlay opens.
   *
   * PrimeNG's `p-treeSelect` defines an `onFilter` output but never emits it.
   * The inner `p-tree` (exposed as `treeViewChild`) does emit `onFilter` on
   * every filter input. We subscribe to it each time the overlay opens
   * because PrimeNG recreates the inner tree on hide/show cycles.
   */
  protected onOrganizationTreeShow(): void {
    this.treeFilterSub?.unsubscribe();
    if (!this.orgTreeSelect?.treeViewChild?.onFilter) {
      return;
    }
    this.treeFilterSub = this.orgTreeSelect.treeViewChild.onFilter.subscribe((event: any) => {
      const query = event?.filter ?? '';
      if (query) {
        this.hasActiveOrgSearch = true;
      }
      this.searchSubject.next(query);
    });
  }

  /**
   * Reset to the full org list when the dropdown closes — but only if the
   * user actually performed a search in this session. Avoids a wasted
   * request every time the user just opens/closes the dropdown.
   */
  protected onOrganizationTreeHide(): void {
    this.treeFilterSub?.unsubscribe();
    this.treeFilterSub = undefined;
    if (this.hasActiveOrgSearch) {
      this.hasActiveOrgSearch = false;
      this.onOrganizationSearch.emit('');
    }
  }

  /**
   * Extract selected organization IDs from the TreeSelect model.
   */
  private getSelectedOrganizationIds(): number[] {
    if (!this.selectedOrganizationsModel || !Array.isArray(this.selectedOrganizationsModel)) {
      return [];
    }
    return this.selectedOrganizationsModel
      .map((node) => (node.key ? parseInt(node.key, 10) : null))
      .filter((id): id is number => id !== null);
  }

  /**
   * Transform flat organization data to PrimeNG TreeNode format.
   *
   * Note: PrimeNG's client-side filter runs on `label` in addition to our
   * server-side search. This is a no-op overhead (instant on ≤100 nodes)
   * and any brief visual flicker during the debounce window is negligible.
   */
  private transformToTreeData(organizations: IOrganization[]): TreeNode[] {
    if (!organizations || organizations.length === 0) {
      return [];
    }

    return organizations.map((org) => ({
      key: org.id.toString(),
      label: org.name,
      data: org,
      selectable: true,
      leaf: true,
    }));
  }
}
